import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import EmergencyPlanEditor from '../components/emergency/EmergencyPlanEditor';
import EmergencyPlanViewer from '../components/emergency/EmergencyPlanViewer';
import AppLogo from '../components/AppLogo';

const EmergencyPlanPage = () => {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [formData, setFormData] = useState({
    emergency_contacts: [], medical_conditions: '', allergies: '',
    current_medications: '', insurance_info: '', preferred_hospital: '',
    primary_care_doctor: '', action_steps: '', important_documents: '',
    special_instructions: ''
  });

  useEffect(() => { checkAccessAndFetchPlan(); }, [circleId]);

  const checkAccessAndFetchPlan = async () => {
    try {
      const [membersRes, meRes] = await Promise.all([
        axios.get(`${API}/circles/${circleId}/members`),
        axios.get(`${API}/auth/me`),
      ]);
      const currentUserId = meRes.data?.id;
      const currentMember = membersRes.data.find(m => (m.user?.id || m.user_id) === currentUserId);

      const role = currentMember.circle_role || currentMember.ring_level;
      if (!currentMember || !['inner', 'primary', 'patient', 'care_coordinator'].includes(role)) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      setHasAccess(true);

      try {
        const planRes = await axios.get(`${API}/circles/${circleId}/emergency-plan`);
        setPlan(planRes.data);
        setFormData({
          emergency_contacts: planRes.data.emergency_contacts || [],
          medical_conditions: planRes.data.medical_conditions || '',
          allergies: planRes.data.allergies || '',
          current_medications: planRes.data.current_medications || '',
          insurance_info: planRes.data.insurance_info || '',
          preferred_hospital: planRes.data.preferred_hospital || '',
          primary_care_doctor: planRes.data.primary_care_doctor || '',
          action_steps: planRes.data.action_steps || '',
          important_documents: planRes.data.important_documents || '',
          special_instructions: planRes.data.special_instructions || ''
        });
      } catch (error) {
        if (error.response?.status === 404) setEditing(true);
      }
    } catch (error) {
      toast.error('Failed to load emergency plan');
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (plan) {
        await axios.put(`${API}/emergency-plans/${plan.id}`, formData);
        toast.success('Emergency plan updated!');
      } else {
        const response = await axios.post(`${API}/emergency-plans`, { ...formData, circle_id: circleId });
        setPlan(response.data);
        toast.success('Emergency plan created!');
      }
      setEditing(false);
      checkAccessAndFetchPlan();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save emergency plan');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="surface-card border-destructive/30 max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertTriangle className="w-16 h-16 text-destructive mx-auto" />
              <h2 className="text-xl font-bold text-foreground">Access Restricted</h2>
              <p className="text-muted-foreground">Emergency plan information is only accessible to inner and middle circle members.</p>
              <Button onClick={() => navigate(`/circles/${circleId}`)} variant="outline">Back to Circle</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="emergency-plan-page">
      <div className="surface-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AppLogo />
              <div>
                <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-destructive" /> Emergency Plan
                </h1>
                <p className="text-sm text-muted-foreground">Critical information for emergency situations</p>
              </div>
            </div>

            {!editing && plan && (
              <Button onClick={() => setEditing(true)} className="btn-primary" data-testid="edit-plan-btn">Edit Plan</Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-destructive">Confidential Information</p>
            <p className="text-sm text-muted-foreground mt-1">This information is only visible to inner and middle circle members.</p>
          </div>
        </div>

        {editing ? (
          <EmergencyPlanEditor formData={formData} setFormData={setFormData} onSave={handleSave} onCancel={() => setEditing(false)} hasExistingPlan={!!plan} />
        ) : plan ? (
          <EmergencyPlanViewer plan={plan} />
        ) : (
          <Card className="surface-card">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-2">No Emergency Plan Yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">Create an emergency plan to ensure everyone knows what to do in a crisis</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EmergencyPlanPage;
